/**
 * In-progress object edits that may need to be flushed onto a page manifest.
 * Mirrors the payload shape consumed by the SET_OBJECTS_ON_PAGE_MANIFEST
 * reducer in LessonPagesProvider.
 */
export interface MirrorableObjects {
  images?: any[];
  textBoxes?: any[];
  annotations?: any[];
  animatedObjects?: any[];
  tables?: any[];
  hotspots?: any[];
  smartObjects?: any[];
  panoramicList?: any[];
  shapeList?: any[];
  videos?: any[];
  objectList?: any[];
  replacedAssets?: any[];
  narrationAudios?: any[];
  audioEffects?: any[];
}

/** Fields the reducer writes regardless of page type. */
function mirrorAlwaysOnFields(pm: any, objects: MirrorableObjects): void {
  if (objects.replacedAssets) pm.replacedAssets = objects.replacedAssets;
  if (objects.images) pm.pageImage = objects.images;
  if (objects.textBoxes) pm.textBlock = objects.textBoxes;
  if (objects.annotations) pm.annotations = objects.annotations;
  if (objects.narrationAudios) pm.narrationAudios = objects.narrationAudios;
}

/** Mirrors the freeForm video + scorm fields, including their delete semantics. */
function mirrorFreeFormMedia(pm: any, objects: MirrorableObjects): void {
  if (objects.videos) {
    if (objects.videos[0]) pm.video = objects.videos[0];
    else if (pm.video) delete pm.video;
  }

  const scorm = objects.objectList?.find((object) => object?.type === "SCORM");
  if (scorm) {
    if (!pm.pageScormObject) pm.pageScormObject = [];
    pm.pageScormObject[0] = scorm;
  } else if (pm.pageScormObject) {
    delete pm.pageScormObject;
  }
}

/** Fields the reducer only writes onto freeForm Base pages. */
function mirrorFreeFormFields(pm: any, objects: MirrorableObjects): void {
  if (objects.hotspots) pm.hotspots = objects.hotspots;
  if (objects.tables) pm.tables = objects.tables;
  if (objects.smartObjects) pm.smartObjects = objects.smartObjects;
  if (objects.panoramicList) pm.panoramicList = objects.panoramicList;
  if (objects.shapeList) pm.shapeList = objects.shapeList;
  if (objects.audioEffects) pm.audioEffects = objects.audioEffects;

  if (objects.animatedObjects) {
    if (!pm.timeline) pm.timeline = { animatedObjects: [], sequenceLength: 30 };
    pm.timeline.animatedObjects = objects.animatedObjects;
  }

  mirrorFreeFormMedia(pm, objects);
}

/**
 * Writes in-progress object edits onto a page manifest in place.
 *
 * `saveLesson` dispatches SET_OBJECTS_ON_PAGE_MANIFEST and then immediately
 * reads `lessonPagesState` before that async dispatch has applied, so it has to
 * mirror the current page's edits onto the manifest synchronously. The original
 * inline block only mirrored a subset of fields (pageImage / textBlock /
 * annotations / timeline / audio), silently dropping tables, hotspots,
 * smartObjects, panoramicList, shapeList, video, scorm and replacedAssets.
 *
 * Centralising the mirror here — and matching the reducer's field set and
 * freeForm gating exactly — guarantees the synchronous save flush can never
 * diverge from the async dispatch again.
 */
export function mirrorObjectsOntoPageManifest(pm: any, objects: MirrorableObjects, isFreeFormBasePage: boolean): void {
  if (!pm) return;
  mirrorAlwaysOnFields(pm, objects);
  if (isFreeFormBasePage) mirrorFreeFormFields(pm, objects);
}
